import { useMemo, useState } from "react";

import { useCreatePackzettel, packzettelDokument } from "../../hooks/usePackzettel";
import DokumentLightbox, { type Dokument } from "../DokumentLightbox";
import { errorText, euro } from "../../lib/invoices";
import {
  kennzahlen,
  type PackzettelPosition,
} from "../../lib/packzettel";

/**
 * Packzettel aus den gewählten Positionen erstellen.
 *
 * Je Position wird ein Budget eingetragen; die Marge rechnet sich **live beim
 * Tippen** mit und wird rot, sobald sie unter 30 % fällt. Die Warnung
 * blockiert bewusst nicht (Entscheidung Benjamin): es gibt gute Gründe für
 * eine dünne Marge, und eine harte Grenze führt dazu, Budgets zu frisieren,
 * statt die Lage zu sehen.
 *
 * Was hier steht, sieht der Subunternehmer NICHT vollständig: `angebot_netto`
 * und die Marge bleiben intern, auf dem Papier steht höchstens sein Budget.
 */

export type PackzettelDialogProps = {
  /**
   * Positionen aus der Auswahl, Budget noch bei 0.
   *
   * Der Dialog wird vom Editor **bedingt gemountet**, nicht per `offen`-Flag
   * versteckt: so beginnt jede Erstellung mit frischen Budgets statt mit den
   * Zahlen der letzten, längst verworfenen Auswahl.
   */
  vorlage: PackzettelPosition[];
  entwurfId: string;
  rechnungId: string;
  /** Vorbelegung des Betreffs, z. B. die Angebotsnummer. */
  betreffVorschlag: string;
  onSchliessen: () => void;
  onErstellt: () => void;
  /**
   * Aus dem Arbeitsplan: wer die Positionen ausführt und gegen welche Pauschale.
   * Ohne diese Angabe (im Angebotseditor) bleibt beides leer — nachtragen geht am Arbeitsplan.
   */
  ausfuehrung?: {
    helfer: { id: string; name: string }[];
    pauschalen: { id: string; helfer_id: string; bezeichnung: string; betrag: number }[];
  };
};

export default function PackzettelDialog({
  vorlage, entwurfId, rechnungId, betreffVorschlag,
  onSchliessen, onErstellt, ausfuehrung,
}: PackzettelDialogProps) {
  const [helferId, setHelferId] = useState("");
  const [pauschaleId, setPauschaleId] = useState("");
  const [positionen, setPositionen] = useState<PackzettelPosition[]>(vorlage);
  const [empfaenger, setEmpfaenger] = useState("");
  const [betreff, setBetreff] = useState(betreffVorschlag);
  const [hinweis, setHinweis] = useState("");
  const [budgetZeigen, setBudgetZeigen] = useState(true);
  const [meldung, setMeldung] = useState("");
  const [dok, setDok] = useState<Dokument>(null);
  const [oeffnet, setOeffnet] = useState(false);
  const anlegen = useCreatePackzettel();

  const zahlen = useMemo(() => kennzahlen(positionen), [positionen]);
  const ohneBudget = positionen.filter((p) => !p.budget_netto).length;

  const setzeBudget = (index: number, wert: number) => {
    setPositionen((vorher) => vorher.map((p, i) => (
      i === index ? { ...p, budget_netto: Number.isFinite(wert) ? wert : 0 } : p
    )));
  };

  const erstellen = async () => {
    setMeldung("");
    let zettel;
    try {
      zettel = await anlegen.mutateAsync({
        entwurf_id: entwurfId || undefined,
        rechnung_id: rechnungId || undefined,
        empfaenger, betreff, hinweis,
        budget_zeigen: budgetZeigen,
        positionen,
        ...(ausfuehrung ? { helfer_id: helferId, pauschale_id: pauschaleId } : {}),
      });
    } catch (fehler) {
      setMeldung(errorText(fehler));
      return;
    }
    onErstellt();
    // Den fertigen Zettel gleich zeigen; mit der Ansicht schließt auch der
    // Dialog. Scheitert nur die Ansicht, ist der Zettel trotzdem angelegt —
    // dann schließt der Dialog sofort, sonst legte ein zweiter Klick einen
    // zweiten Zettel an. Er steht in der Packzettel-Liste.
    setOeffnet(true);
    try {
      setDok(await packzettelDokument(zettel.id, zettel.nummer));
    } catch {
      onSchliessen();
    } finally {
      setOeffnet(false);
    }
  };
  const ansichtZu = () => {
    if (dok?.url.startsWith("blob:")) URL.revokeObjectURL(dok.url);
    setDok(null);
    onSchliessen();
  };

  return (
    <div
      className="invoice-ablage-backdrop"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onSchliessen(); }}
    >
      <aside className="invoice-packzettel" role="dialog" aria-modal="true" aria-labelledby="pz-titel">
        <header className="invoice-ablage-head">
          <div>
            <p className="invoice-kicker">Für einen Subunternehmer</p>
            <h2 id="pz-titel">Packzettel erstellen</h2>
          </div>
          <button type="button" className="invoice-button" onClick={onSchliessen}>Schließen</button>
        </header>

        {meldung && <p className="invoice-pz-fehler" role="alert">{meldung}</p>}

        <div className="invoice-pz-body">
          <div className="invoice-pz-kopf">
            <label className="invoice-editor-field">
              <span>Empfänger</span>
              <textarea
                rows={3}
                value={empfaenger}
                onChange={(event) => setEmpfaenger(event.target.value)}
                placeholder={"Firma\nStraße\nPLZ Ort"}
              />
            </label>
            <label className="invoice-editor-field">
              <span>Betreff</span>
              <input
                value={betreff}
                onChange={(event) => setBetreff(event.target.value)}
                placeholder="z. B. Pflasterarbeiten Musterweg 3"
              />
            </label>
          </div>

          {ausfuehrung && (
            <div className="invoice-pz-kopf">
              <label className="invoice-editor-field">
                <span>Wer führt aus</span>
                <select value={helferId} onChange={(event) => { setHelferId(event.target.value); setPauschaleId(""); }}>
                  <option value="">noch niemand</option>
                  {ausfuehrung.helfer.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
                </select>
              </label>
              <label className="invoice-editor-field">
                <span>Pauschale</span>
                <select value={pauschaleId} disabled={!helferId} onChange={(event) => setPauschaleId(event.target.value)}>
                  <option value="">{helferId ? "keine — nach Stunden oder später" : "erst den Helfer wählen"}</option>
                  {ausfuehrung.pauschalen.filter((p) => p.helfer_id === helferId).map((p) => (
                    <option key={p.id} value={p.id}>{p.bezeichnung || "Pauschale"} · {euro(p.betrag)}</option>
                  ))}
                </select>
              </label>
            </div>
          )}

          <table className="invoice-pz-tabelle">
            <thead>
              <tr>
                <th>Leistung</th>
                <th className="r">Menge</th>
                <th className="r">Angebot netto</th>
                <th className="r">Budget netto</th>
                <th className="r">Marge</th>
              </tr>
            </thead>
            <tbody>
              {positionen.map((p, index) => {
                const marge = p.angebot_netto - p.budget_netto;
                const prozent = p.angebot_netto > 0
                  ? Math.round((marge / p.angebot_netto) * 1000) / 10
                  : null;
                return (
                  <tr key={`${p.titel}-${index}`}>
                    <td>
                      <strong>{p.titel}</strong>
                      {p.beschreibung && <small>{p.beschreibung}</small>}
                    </td>
                    <td className="r" data-label="Menge">{p.menge} {p.einheit}</td>
                    {/* Unser Preis steht hier zum Vergleich — er geht NICHT
                        ins PDF, siehe packzettel_pdf.rendern. */}
                    <td className="r intern" data-label="Angebot netto">{euro(p.angebot_netto)}</td>
                    <td className="r" data-label="Budget netto">
                      <div className="invoice-money-input">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={p.budget_netto || ""}
                          onChange={(event) => setzeBudget(index, Number(event.target.value))}
                          placeholder="0,00"
                          aria-label={`Budget für ${p.titel}`}
                        />
                        <b>€</b>
                      </div>
                    </td>
                    <td className={"r" + (prozent !== null && prozent < 30 ? " warn" : "")} data-label="Marge">
                      {prozent === null ? "—" : `${prozent} %`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <label className="invoice-editor-field">
            <span>Hinweise für den Empfänger (optional)</span>
            <textarea
              rows={2}
              value={hinweis}
              onChange={(event) => setHinweis(event.target.value)}
              placeholder="Termin, Ansprechpartner, Zufahrt …"
            />
          </label>

          <label className="invoice-pz-schalter">
            <input
              type="checkbox"
              checked={budgetZeigen}
              onChange={(event) => setBudgetZeigen(event.target.checked)}
            />
            <span>
              Budget auf den Packzettel drucken
              <small>
                Ohne Haken bekommt der Empfänger eine reine Leistungsliste. Unser
                Angebotspreis und die Marge stehen so oder so nie darauf.
              </small>
            </span>
          </label>
        </div>

        <footer className="invoice-pz-fuss">
          <div className={"invoice-pz-marge" + (zahlen.unter_mindestmarge ? " is-warn" : "")}>
            <span>Marge, die bei uns bleibt</span>
            <strong>
              {zahlen.bewertbar ? `${zahlen.marge_prozent} %` : "—"}
              <small>{euro(zahlen.marge_netto)}</small>
            </strong>
            {zahlen.unter_mindestmarge && (
              <em>unter {zahlen.mindestmarge} % — erstellen geht trotzdem</em>
            )}
            {ohneBudget > 0 && (
              <em className="is-hinweis">
                {ohneBudget === 1
                  ? "Eine Position hat noch kein Budget"
                  : `${ohneBudget} Positionen haben noch kein Budget`}
              </em>
            )}
          </div>
          <button
            type="button"
            className="invoice-button primary"
            onClick={erstellen}
            disabled={anlegen.isPending || oeffnet || positionen.length === 0}
          >
            {anlegen.isPending ? "Erstellt …" : oeffnet ? "Öffnet …" : "Packzettel erstellen und öffnen"}
          </button>
        </footer>
      </aside>
      <DokumentLightbox dok={dok} onClose={ansichtZu} />
    </div>
  );
}
